import { Fragment, useCallback, useMemo } from 'react';

import { formatDate } from '@zola-helpers/client/dist/es/util/dateUtils';
import FilterPill from '@zola/zola-ui/src/components/FilterPill/FilterPill';
import { FilterPillContainer } from '@zola/zola-ui/src/components/FilterPill/FilterPill.styles';
import { ClearBtn } from '@zola/zola-ui/src/components/FilterPill/FilterPills.styles';

import styled from '@emotion/styled';

import { createVendorSearchFromFilters } from '~/pages/couples/explore/util/createVendorSearchFromFilters';
import { OptionFacetType } from '~/types/types';

import { useSearchResultsPageContext } from '../../contexts/SearchResultsPageContext';
import { useVendorSearchNavigation } from '../../contexts/VendorSearchNavigationContext';

const FilterPills = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;

  ${FilterPillContainer} {
    margin: 0;
  }
`;

export const AppliedFilters = () => {
  const { navigateToSrp } = useVendorSearchNavigation();

  const { selectedFacetOptions, vendorSearch } = useSearchResultsPageContext();

  const clearFilters = useCallback(() => {
    navigateToSrp({
      awardGroups: null,
      selectedFacets: null,
      price: null,
      capacity: null,
      searchTerm: null,
      availabilityDates: null,
      metroTypes: null,
      catererTypes: null,
      page: undefined,
    });
  }, [navigateToSrp]);

  const setSelectedFitlers = useCallback(
    (options: OptionFacetType[]) => navigateToSrp(createVendorSearchFromFilters(options)),
    [navigateToSrp]
  );

  const clearFilter = useCallback(
    (facetSlug: string) => {
      const filteredRequestFacets = selectedFacetOptions.filter(
        (facet) => facet.slug !== facetSlug
      );
      setSelectedFitlers(filteredRequestFacets);
    },
    [selectedFacetOptions, setSelectedFitlers]
  );

  const clearAvailabilityDate = useCallback(
    (dateToRemove: string) => {
      navigateToSrp({
        availabilityDates: vendorSearch.availabilityDates?.filter((date) => date !== dateToRemove),
      });
    },
    [navigateToSrp, vendorSearch.availabilityDates]
  );

  const options = useMemo(() => {
    const optionsFromFacets = (selectedFacetOptions || []).map((facet) => ({
      label: facet.tagName || facet.displayName || facet.name,
      value: facet.slug,
      onClick: () => clearFilter(facet.slug),
    }));

    const optionsFromDates = (vendorSearch?.availabilityDates || []).map((date) => ({
      label: `Available on ${formatDate(date, 'MMMM D, YYYY')}`,
      value: date,
      onClick: () => clearAvailabilityDate(date),
    }));

    return [...optionsFromFacets, ...optionsFromDates];
  }, [clearAvailabilityDate, clearFilter, selectedFacetOptions, vendorSearch?.availabilityDates]);

  if (options.length === 0) {
    return <Fragment />;
  }

  return (
    <FilterPills>
      {options.map((option, index) => {
        return (
          <FilterPill key={index} value={option.value} handlePillClick={option.onClick}>
            {option.label}
          </FilterPill>
        );
      })}
      <ClearBtn onClick={clearFilters}>Clear</ClearBtn>
    </FilterPills>
  );
};
